import { Add as AddIcon, FileDownload as ExportIcon, FileUpload as ImportIcon } from "@mui/icons-material";
import { Alert, Box, Button, Card, CircularProgress, Container, Grid, Snackbar, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { EditDialog, LivestreamCard, VideoCard } from "src/components/videos";
import type { Video, VideosResponse } from "src/components/videos/types";
import { useVideoCategories } from "src/hooks/useVideoCategories";
import { useVideos } from "src/hooks/useVideos";

export default function VideosPage() {
	const {
		videosData,
		isLoading: videosLoading,
		importVideos,
		isImporting,
		createVideo,
		updateVideo,
		updateLivestream,
		deleteVideo: deleteVideoAPI,
		isCreating,
		isUpdating,
		isDeleting,
	} = useVideos();

	const { categories, isLoading: categoriesLoading } = useVideoCategories();

	const [editDialog, setEditDialog] = useState<{
		open: boolean;
		type: "livestream" | "video";
		data: Video | null;
		isNew: boolean;
	}>({
		open: false,
		type: "livestream",
		data: null,
		isNew: false,
	});
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success" as "success" | "error",
	});

	// Default data for when videosData is undefined
	const defaultVideosData: VideosResponse = {
		livestream: {
			category_id: 0,
			category: "",
			title: "",
			description: "",
			image_uri: "",
			video_uri: "",
			order_index: 0,
			is_live_stream: true,
			status: 1,
		},
		videos: [],
	};

	const currentData = videosData || defaultVideosData;

	// Show loading screen while initial data is loading
	if (videosLoading && !videosData) {
		return (
			<Container>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
					<Stack spacing={2} alignItems="center">
						<CircularProgress />
						<Typography>Loading videos...</Typography>
					</Stack>
				</Box>
			</Container>
		);
	}

	const showSnackbar = (message: string, severity: "success" | "error") => {
		setSnackbar({ open: true, message, severity });
	};

	const handleEditLivestream = () => {
		setEditDialog({
			open: true,
			type: "livestream",
			data: { ...currentData.livestream },
			isNew: false,
		});
	};

	const handleEditVideo = (video: Video) => {
		setEditDialog({
			open: true,
			type: "video",
			data: { ...video },
			isNew: false,
		});
	};

	const handleAddVideo = () => {
		const newVideo: Video = {
			category_id: 1, // Default category
			category: "", // Default empty category name
			title: "",
			description: "",
			image_uri: "",
			video_uri: "",
			order_index: currentData.videos.length + 1,
			is_live_stream: false,
			status: 1,
		};
		setEditDialog({
			open: true,
			type: "video",
			data: newVideo,
			isNew: true,
		});
	};

	const handleDeleteVideo = (videoId: number) => {
		if (window.confirm("Are you sure you want to delete this video?")) {
			try {
				deleteVideoAPI(videoId);
				showSnackbar("Video deleted successfully", "success");
			} catch (error) {
				console.error("Error deleting video:", error);
				showSnackbar("Error deleting video", "error");
			}
		}
	};

	const handleSaveDialog = () => {
		if (!editDialog.data) return;

		try {
			if (editDialog.type === "livestream") {
				// For livestream, we'll use the import functionality for now
				// In a real implementation, you might have a separate livestream update endpoint
				const updatedLivestream = editDialog.data as Video;
				const updatedData = {
					livestream: updatedLivestream,
					videos: currentData.videos,
				};
				importVideos(updatedData);
				// const { video_id, ...livestream } = updatedLivestream;
				// updateLivestream({ videoId: video_id ?? -1, video: livestream });
				showSnackbar("Livestream updated successfully", "success");
			} else {
				// For videos, use individual API endpoints
				const videoData = editDialog.data as Video;
				if (editDialog.isNew) {
					const { video_id, ...videoToCreate } = videoData;
					createVideo(videoToCreate);
					showSnackbar("Video created successfully", "success");
				} else if (videoData.video_id) {
					updateVideo({
						videoId: videoData.video_id,
						video: videoData,
					});
					showSnackbar("Video updated successfully", "success");
				}
			}

			setEditDialog({
				open: false,
				type: "livestream",
				data: null,
				isNew: false,
			});
		} catch (error) {
			console.error("Error saving video:", error);
			showSnackbar("Error saving video", "error");
		}
	};

	const handleDialogFieldChange = (field: string, value: string) => {
		setEditDialog((prev) => {
			if (!prev.data) return prev;

			// Handle field mapping for new Video interface
			const updatedData = { ...prev.data };

			if (field === "category_id") {
				updatedData.category_id = Number(value);
				updatedData.category = categories.find((cat) => cat.video_category_id.toString() === value)?.category_name || "";
			} else {
				(updatedData as any)[field] = value;
			}

			return {
				...prev,
				data: updatedData,
			};
		});
	};

	const handleExportData = () => {
		// Create export data with category names included for both livestream and videos
		const exportData = {
			livestream: {
				...currentData.livestream,
				category: currentData.livestream.category || `Category ${currentData.livestream.category_id}`, // Ensure category name is included
			},
			videos: currentData.videos.map((video) => ({
				...video,
				category: video.category || `Category ${video.category_id}`, // Ensure category name is included
			})),
		};

		const dataStr = JSON.stringify(exportData, null, 2);
		const dataBlob = new Blob([dataStr], { type: "application/json" });
		const url = URL.createObjectURL(dataBlob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `videos-config-${new Date().toISOString().slice(0, 10)}.json`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
		showSnackbar("Data exported", "success");
	};

	const handleImportData = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = async (e) => {
			try {
				const importedData = JSON.parse(e.target?.result as string);

				// Data structure validation
				if (importedData.livestream && importedData.videos && Array.isArray(importedData.videos)) {
					// Convert imported data to match new Video interface
					// Include both category_id and category name for compatibility
					const convertedData: VideosResponse = {
						livestream: {
							category_id: importedData.livestream.category_id || 0,
							category: importedData.livestream.category || "livestream", // Include category name from import for livestream
							title: importedData.livestream.title || "",
							description: importedData.livestream.description || "",
							image_uri: importedData.livestream?.image_uri || "",
							video_uri: importedData.livestream?.video_uri || "",
							order_index: 0,
							is_live_stream: true,
							status: importedData.livestream.status || 1,
						},
						videos: importedData.videos.map((video: any, index: number) => ({
							video_id: video.video_id,
							category_id: video.category_id || 1,
							category: video.category || "", // Include category name from import
							title: video.title || "",
							description: video.description || "",
							image_uri: video?.image_uri || "",
							video_uri: video?.video_uri || "",
							order_index: video.order_index || index + 1,
							is_live_stream: false,
							status: video.status || 1,
						})),
					};

					// Use import API to save imported data
					importVideos(convertedData);
					showSnackbar("Data imported successfully", "success");
				} else {
					throw new Error("Invalid file structure");
				}
			} catch (error) {
				console.error("Import error:", error);
				showSnackbar("Import error: invalid file format", "error");
			}
		};
		reader.readAsText(file);

		// Clear input for possible re-import of the same file
		event.target.value = "";
	};

	return (
		<>
			<Helmet>
				<title>Videos Configuration</title>
			</Helmet>

			<Container>
				<Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
					<Stack>
						<Typography variant="h4" gutterBottom>
							Videos Configuration
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Videos in collection: {currentData.videos.length}
						</Typography>
					</Stack>
					<Stack direction="row" spacing={1}>
						<input accept="application/json" style={{ display: "none" }} id="import-json-file" type="file" onChange={handleImportData} />
						<label htmlFor="import-json-file">
							<Button variant="outlined" component="span" startIcon={<ImportIcon />} size="small" disabled={isImporting}>
								{isImporting ? "Importing..." : "Import"}
							</Button>
						</label>
						<Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExportData} size="small">
							Export
						</Button>
					</Stack>
				</Stack>

				<LivestreamCard
					livestream={{
						image_uri: currentData.livestream?.image_uri,
						title: currentData.livestream?.title,
						description: currentData.livestream?.description,
						video_uri: currentData.livestream?.video_uri,
						order_index: currentData.livestream?.order_index.toString(),
						status: currentData.livestream?.status ?? 0,
					}}
					onEdit={handleEditLivestream}
				/>

				{/* Videos секція */}
				<Card sx={{ p: 3 }}>
					<Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
						<Typography variant="h5">Video Collection</Typography>
						<Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddVideo} disabled={isCreating}>
							Add Video
						</Button>
					</Stack>

					<Grid container spacing={3}>
						{currentData.videos.length > 0 ? (
							currentData.videos.map((video) => (
								<Grid item xs={12} sm={6} md={4} key={video.video_id || video.title}>
									<VideoCard
										video={{
											id: video.video_id?.toString() || "",
											image_uri: video?.image_uri,
											title: video?.title,
											category: video?.category || "",
											category_id: video?.category_id,
											video_uri: video?.video_uri,
											order_index: video?.order_index.toString(),
											status: video?.status ?? 0,
										}}
										onEdit={() => handleEditVideo(video)}
										onDelete={() => video.video_id && handleDeleteVideo(video.video_id)}
										isDeleting={isDeleting}
									/>
								</Grid>
							))
						) : (
							<Grid item xs={12}>
								<Card
									sx={{
										p: 6,
										textAlign: "center",
										backgroundColor: "#f8f9fa",
										border: "2px dashed #dee2e6",
									}}
								>
									<Typography variant="h6" color="textSecondary" gutterBottom>
										📹 Video collection is empty
									</Typography>
									<Typography variant="body2" color="textSecondary" mb={3}>
										Add your first video to start building the collection
									</Typography>
									<Button variant="contained" startIcon={<AddIcon />} onClick={handleAddVideo} disabled={isCreating}>
										Add First Video
									</Button>
								</Card>
							</Grid>
						)}
					</Grid>
				</Card>

				<EditDialog
					open={editDialog.open}
					type={editDialog.type}
					data={
						editDialog.data
							? {
									// Convert Video to legacy format for EditDialog
									...(editDialog.type === "livestream"
										? {
												image_uri: editDialog.data.image_uri,
												title: editDialog.data.title,
												description: editDialog.data.description,
												video_uri: editDialog.data.video_uri,
												order_index: editDialog.data.order_index?.toString(),
												status: editDialog.data.status,
											}
										: {
												id: editDialog.data.video_id?.toString() || "",
												image_uri: editDialog.data.image_uri,
												title: editDialog.data.title,
												category: editDialog.data.category || "",
												category_id: editDialog.data.category_id,
												video_uri: editDialog.data.video_uri,
												order_index: editDialog.data.order_index?.toString(),
												status: editDialog.data.status,
											}),
								}
							: null
					}
					isNew={editDialog.isNew}
					onClose={() =>
						setEditDialog({
							open: false,
							type: "livestream",
							data: null,
							isNew: false,
						})
					}
					onSave={handleSaveDialog}
					onFieldChange={handleDialogFieldChange}
					isLoading={isCreating || isUpdating}
					categories={categories}
					categoriesLoading={categoriesLoading}
				/>

				<Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
					<Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: "100%" }}>
						{snackbar.message}
					</Alert>
				</Snackbar>
			</Container>
		</>
	);
}
